/**
 * Pricing Price — the amount, period and note, one set per option.
 *
 * The per-option array stays on this block rather than on the plan: it is this
 * part's own data, and keeping it here is what makes selecting the price give
 * you the price's controls and nothing else.
 *
 * Every option's row is rendered; the table's stylesheet hides all but the one
 * whose radio is checked — the same rule in the editor as on the front end, so
 * clicking an option on the canvas switches the price for real.
 */
import { __ } from '@wordpress/i18n';
import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, RichText } from '@wordpress/block-editor';
import { useEffect } from '@wordpress/element';
import metadata from './block.json';
import { alignPrices, MAX_PRICING_OPTIONS } from '../_shared/pricing-defaults';

registerBlockType(metadata.name, {
  __experimentalLabel: () => __('Price', 'brandy-blocks'),

  edit: ({ attributes, setAttributes, context }) => {
    const { prices } = attributes;

    const options = (context['brandy/pricingTerms'] || []).slice(
      0,
      MAX_PRICING_OPTIONS
    );
    const termCount = Math.max(1, options.length);

    // The table owns how many terms exist, so this follows. Padding rather
    // than replacing: adding a term must not wipe the price already written.
    useEffect(() => {
      const aligned = alignPrices(prices, termCount);
      if (aligned.length !== (prices || []).length) {
        setAttributes({ prices: aligned });
      }
    }, [termCount, prices, setAttributes]);

    const rows = alignPrices(prices, termCount);

    const update = (index, patch) =>
      setAttributes({
        prices: rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
      });

    const blockProps = useBlockProps({
      className: 'brandy-pricing-plan__prices',
    });

    return (
      <div {...blockProps}>
        {rows.map((row, index) => (
          <div
            key={index}
            className="brandy-pricing-plan__price"
            data-term={index}
          >
            <p className="brandy-pricing-plan__amount-row">
              <RichText
                tagName="span"
                className="brandy-pricing-plan__amount"
                value={row.amount}
                onChange={(value) => update(index, { amount: value })}
                placeholder={__('$0', 'brandy-blocks')}
              />
              <RichText
                tagName="span"
                className="brandy-pricing-plan__period"
                value={row.period}
                onChange={(value) => update(index, { period: value })}
                placeholder={__('/month', 'brandy-blocks')}
              />
            </p>
            <RichText
              tagName="p"
              className="brandy-pricing-plan__note"
              value={row.note}
              onChange={(value) => update(index, { note: value })}
              placeholder={__('Billing note', 'brandy-blocks')}
            />
          </div>
        ))}
      </div>
    );
  },

  save: () => null,
});
